import { useState } from "react";
import { toast } from "sonner";
import { useStore } from "../context/StoreContext";
import Coin from "./Coin";
import sfx, { VOICE_PRESETS } from "../utils/sound";

// Each tier costs coins and stretches the "SIX ... SEVENNN!" shout longer.
// Levels map 1:1 with sfx.sixSeven(level) intensity knobs.
const TIERS = [
  {
    level: 1,
    cost: 5,
    label: "Say it",
    tag: "Quick shout",
    bg: "#FFB400",
  },
  {
    level: 2,
    cost: 20,
    label: "Say it LOUD",
    tag: "Stretched out",
    bg: "#F368E0",
  },
  {
    level: 3,
    cost: 50,
    label: "Full shout",
    tag: "Extra drawn out",
    bg: "#54A0FF",
  },
  {
    level: 4,
    cost: 100,
    label: "MEGA 67",
    tag: "Double six + long sevennn",
    bg: "#5F27CD",
  },
];

// Cheap solo-number shouts — one word only. Level determines vowel stretch.
const SOLO_SHOUTS = [
  { word: "six", label: "Just Six", cost: 2, level: 1, bg: "#54D62C" },
  { word: "seven", label: "Just Seven", cost: 3, level: 2, bg: "#FF9F1A" },
];

const SixtySevenBooth = () => {
  const { coins, logSixSeven, boothVoice, setBoothVoice } = useStore();
  const [pulsing, setPulsing] = useState(null); // level currently playing

  const shout = (tier) => {
    if (coins < tier.cost) {
      toast(`Need ${tier.cost - coins} more coins!`, { duration: 1600 });
      return;
    }
    // Bill + record achievements in one atomic state update, then play sound.
    logSixSeven(tier.level, tier.cost);
    sfx.sixSeven(tier.level);
    setPulsing(tier.level);
    // approximate playback length by level: 0.9 / 1.7 / 2.6 / 4.0s
    const dur = [900, 1700, 2600, 4000][tier.level - 1] || 900;
    setTimeout(() => setPulsing((p) => (p === tier.level ? null : p)), dur);
  };

  const soloShout = (shoutCfg) => {
    if (coins < shoutCfg.cost) {
      toast(`Need ${shoutCfg.cost - coins} more coins!`, { duration: 1600 });
      return;
    }
    // Reuse the same billing hook — passes tier=level so achievements track.
    logSixSeven(shoutCfg.level, shoutCfg.cost);
    sfx.sayNumber(shoutCfg.word, shoutCfg.level);
  };

  return (
    <div
      className="chunky-card p-5 sm:p-6 relative overflow-hidden"
      style={{
        background:
          "radial-gradient(circle at 15% 20%, #FFF3B0 0%, #FFE6E6 55%, #E0F0FF 100%)",
      }}
      data-testid="six-seven-booth"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div
          className={`rounded-full flex items-center justify-center shrink-0 ${
            pulsing ? "pulse-67" : "bob"
          }`}
          style={{
            width: 72,
            height: 72,
            background: "#FF5A5F",
            border: "3px solid #2b2b2b",
            boxShadow: "0 4px 0 #2b2b2b",
            fontFamily: "Fredoka, sans-serif",
            fontWeight: 700,
            fontSize: 30,
            color: "#fff",
          }}
        >
          67
        </div>
        <div className="flex-1 min-w-[180px]">
          <div
            className="chunky-badge mb-2"
            style={{ background: "#FFB400", color: "#2b2b2b" }}
          >
            🎤 The 67 Booth
          </div>
          <h3 className="font-display text-xl sm:text-2xl leading-tight">
            Pay coins, hear the shout!
          </h3>
          <p className="text-sm sm:text-base mt-1" style={{ color: "#484848" }}>
            The more coins you spend, the longer the &ldquo;sevennnn&rdquo; goes.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
        {TIERS.map((t) => {
          const disabled = coins < t.cost;
          return (
            <button
              key={t.level}
              type="button"
              onClick={() => shout(t)}
              disabled={disabled}
              data-testid={`six-seven-tier-${t.level}`}
              className="chunky-card pushable p-3 text-left flex flex-col gap-1"
              style={{
                background: t.bg + "22",
                borderColor: "#2b2b2b",
                opacity: disabled ? 0.55 : 1,
                cursor: disabled ? "not-allowed" : "pointer",
              }}
            >
              <div
                className="inline-flex items-center gap-1 rounded-full self-start px-2 py-0.5"
                style={{
                  background: t.bg,
                  border: "2px solid #2b2b2b",
                  color: "#2b2b2b",
                  fontFamily: "Fredoka, sans-serif",
                  fontWeight: 600,
                  fontSize: 12,
                }}
              >
                <Coin size={12} />
                {t.cost}
              </div>
              <div className="font-display text-base sm:text-lg leading-tight">
                {t.label}
              </div>
              <div className="text-xs" style={{ color: "#484848" }}>
                {t.tag}
              </div>
            </button>
          );
        })}
      </div>

      {/* Solo shouts — cheap one-word buttons for when kids just want to hear
          one of the two numbers on its own. Uses sfx.sayNumber(). */}
      <div className="mt-3 grid grid-cols-2 gap-3" data-testid="six-seven-solo-row">
        {SOLO_SHOUTS.map((s) => {
          const disabled = coins < s.cost;
          return (
            <button
              key={s.word}
              type="button"
              onClick={() => soloShout(s)}
              disabled={disabled}
              data-testid={`six-seven-solo-${s.word}`}
              className="chunky-card pushable p-3 flex items-center justify-between gap-2"
              style={{
                background: s.bg + "33",
                borderColor: "#2b2b2b",
                opacity: disabled ? 0.55 : 1,
                cursor: disabled ? "not-allowed" : "pointer",
              }}
            >
              <span className="font-display text-base sm:text-lg">{s.label}</span>
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5"
                style={{
                  background: s.bg,
                  border: "2px solid #2b2b2b",
                  color: "#2b2b2b",
                  fontFamily: "Fredoka, sans-serif",
                  fontWeight: 600,
                  fontSize: 12,
                }}
              >
                <Coin size={12} />
                {s.cost}
              </span>
            </button>
          );
        })}
      </div>

      {/* Voice picker — swap between girl / boy shouters. Free to try; the
          currently-picked voice sings every shout above. */}
      <div className="mt-4" data-testid="six-seven-voice-picker">
        <div
          className="text-xs mb-2 font-display"
          style={{ color: "#484848" }}
        >
          Pick a voice
        </div>
        <div className="grid grid-cols-4 gap-2">
          {VOICE_PRESETS.map((v) => {
            const active = boothVoice === v.id;
            return (
              <button
                key={v.id}
                type="button"
                data-testid={`voice-pick-${v.id}`}
                aria-pressed={active}
                onClick={() => {
                  setBoothVoice(v.id);
                  // Instant preview so kids hear the change immediately.
                  sfx.sayNumber("six", 1, v.id);
                }}
                className="chunky-card pushable p-2 text-center"
                style={{
                  background: active ? "#FFB400" : "#fff",
                  borderColor: "#2b2b2b",
                  cursor: "pointer",
                }}
              >
                <div style={{ fontSize: 22 }} aria-hidden="true">
                  {v.emoji}
                </div>
                <div
                  className="text-xs mt-1"
                  style={{
                    fontFamily: "Fredoka, sans-serif",
                    fontWeight: 600,
                    color: "#2b2b2b",
                  }}
                >
                  {v.label}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SixtySevenBooth;
